import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import {
  TrendingUp, Building2, DollarSign, Wrench, PlayCircle, Sparkles,
  BookOpen, MessageCircle, ChevronRight,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Owner Portal — calm executive dashboard with embedded onboarding video,
// contextual help, AI insights, and downloadable reports.
// Inspired by appfolio.com/help/owner but operationally tightened.
// Route: /owner-portal (owner role)
// ─────────────────────────────────────────────────────────────────────────────
export default function OwnerPortal() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState({ properties: 0, units: 0, occupancy: 0, monthlyNoi: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [props, units] = await Promise.all([api.getProperties(), api.getUnits()]);
        const propCount = props.data?.length || 0;
        const unitArr = units.data || [];
        const occupied = unitArr.filter(u => u.status === 'occupied').length;
        const occupancy = unitArr.length ? Math.round((occupied / unitArr.length) * 100) : 0;
        const monthlyNoi = unitArr.reduce((s, u) => s + (u.monthly_rent || 0), 0);
        setKpis({ properties: propCount, units: unitArr.length, occupancy, monthlyNoi });
      } catch { /* silent — KPIs are best-effort */ }
    })();
  }, []);

  return (
    <Layout>
      <div className="max-w-[1240px] mx-auto px-6 py-8" data-testid="owner-portal-page">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
              <Building2 className="h-3 w-3" /> Owner Portal
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[#0E1320]">
              Welcome, {user?.full_name || 'Owner'}.
            </h1>
            <p className="mt-1.5 text-[14px] text-[#4A5263] max-w-2xl">
              Your executive view of the portfolio. Cash flow, occupancy, AI insights, and downloadable reports — without operational noise.
            </p>
          </div>
        </div>

        {/* Onboarding video card */}
        <div className="rounded-2xl border border-[#E6EAF0] bg-gradient-to-br from-[#0E1320] to-[#1A2238] text-white p-6 lg:p-8 mb-8 relative overflow-hidden" data-testid="owner-onboarding-card">
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}
          />
          <div className="relative z-10 grid md:grid-cols-[1fr_auto] items-center gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10.5px] reva-mono uppercase tracking-[0.18em]">
                <Sparkles className="h-3 w-3" /> Onboarding · 90 seconds
              </div>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight">Get the most out of your Owner Portal</h2>
              <p className="mt-2 text-[13.5px] text-white/75 max-w-xl">
                A quick guided tour of cash-flow views, AI insights, monthly investor-ready reports, and where to find your portfolio health score.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white text-[#0E1320] text-[12.5px] font-medium hover:bg-white/90 transition-colors" data-testid="owner-watch-tour">
                  <PlayCircle className="h-4 w-4" /> Watch the tour
                </button>
                <button className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15 text-[12.5px] font-medium hover:bg-white/15 transition-colors" data-testid="owner-help-center">
                  <BookOpen className="h-4 w-4" /> Help center
                </button>
                <button className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15 text-[12.5px] font-medium hover:bg-white/15 transition-colors" data-testid="owner-ai-assistant">
                  <MessageCircle className="h-4 w-4" /> Ask REVA AI
                </button>
              </div>
            </div>
            <div className="hidden md:block w-72 h-44 rounded-2xl bg-black/30 border border-white/10 flex items-center justify-center">
              <PlayCircle className="h-14 w-14 text-white/70" strokeWidth={1.4} />
            </div>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { k: 'properties', label: 'Properties', val: kpis.properties, icon: Building2 },
            { k: 'units',      label: 'Units',      val: kpis.units,      icon: Building2 },
            { k: 'occupancy',  label: 'Occupancy',  val: `${kpis.occupancy}%`, icon: TrendingUp },
            { k: 'monthly_noi',label: 'Monthly cash flow · SAR', val: kpis.monthlyNoi.toLocaleString(), icon: DollarSign },
          ].map(({ k, label, val, icon: Icon }) => (
            <div key={k} className="rounded-xl border border-[#E6EAF0] bg-white p-4" data-testid={`owner-kpi-${k}`}>
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4 text-[#00734A]" strokeWidth={1.7} />
                <span className="text-[10px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</span>
              </div>
              <div className="mt-3 text-2xl font-semibold text-[#0E1320]">{val}</div>
            </div>
          ))}
        </div>

        {/* Help / quick links */}
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { title: 'Understanding your cash flow report', desc: 'How NOI, vacancies, and operating expenses combine into your monthly position.', icon: DollarSign },
            { title: 'Reading the portfolio health score', desc: 'What drives the score — occupancy, collections velocity, maintenance backlog, lease expirations.', icon: TrendingUp },
            { title: 'Approving a maintenance escalation', desc: 'When your property manager flags a vendor cost above threshold, how to review and approve quickly.', icon: Wrench },
            { title: 'Downloading the monthly investor report', desc: 'A board-ready Arabic+English PDF you can forward to capital partners.', icon: BookOpen },
          ].map((h) => (
            <a key={h.title} href="#" className="group rounded-xl border border-[#E6EAF0] bg-white p-4 hover:border-[#0E1320] transition-colors flex items-start gap-3" data-testid={`owner-help-${h.title.toLowerCase().replace(/\s+/g, '-').slice(0,30)}`}>
              <div className="h-8 w-8 rounded-full bg-[#F1F4F7] flex items-center justify-center shrink-0">
                <h.icon className="h-4 w-4 text-[#0E1320]" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-[#0E1320]">{h.title}</div>
                <p className="mt-0.5 text-[12.5px] text-[#4A5263]">{h.desc}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-[#71777E] group-hover:text-[#0E1320] transition-colors" />
            </a>
          ))}
        </div>
      </div>
    </Layout>
  );
}
